import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import OrganizationsForm from "./OrganizationsForm";
import { useOrganizations } from "../../hooks/useOrganizations";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuthContext } from "../../context/AuthProvider";

const OrganizationsDashboard = () => {
    const [open, setOpen] = useState(false);
    const [editOrganization, setEditOrganization] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState(null);

    const { user } = useAuthContext();
    const { organizations, isLoading, error, createMutation, updateMutation, deleteMutation } = useOrganizations();

    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const handleSubmitOrganization = (data) => {
        if (editOrganization) {            
            updateMutation.mutate(
                { id: editOrganization.orgid, data: { ...data, updbyid: user.EmpID } },
                {
                    onSuccess: () => {
                        showAlert("Organización actualizada con éxito");
                        setOpen(false);
                        setEditOrganization(null);
                    },
                    onError: (error) => {
                        showAlert(error.message || "Error al actualizar la organización", "error");
                    },
                }
            );
        } else {
            data.crebyid = user.EmpID;
            createMutation.mutate(data, {
                onSuccess: () => {
                    showAlert("Organización creada con éxito");
                    setOpen(false);
                },
                onError: (error) => {
                    showAlert(error.message || "Error al crear la organización", "error");
                },
            });
        }
    };

    const handleOpenConfirm = (organization) => {
        setSelectedOrganization(organization);
        setOpenConfirm(true);
    };

    const handleConfirmDelete = () => {
        if (selectedOrganization) {
            deleteMutation.mutate(selectedOrganization.orgid, {
                onSuccess: () => {
                    showAlert("Organización eliminada con éxito");
                    setOpenConfirm(false);
                    setSelectedOrganization(null);
                },
                onError: (error) => {
                    showAlert(error.response.data.error|| "Error al eliminar la organización", "error");
                },
            });
        }
    };

    if (isLoading) return <Typography>Cargando organizaciones...</Typography>;
    if (error) return <Typography>Error al cargar organizaciones: {error.message}</Typography>;

    return (
        <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Clientes</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditOrganization(null);
                        setOpen(true);
                    }}
                >
                    Nueva Organización
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Nombre</strong></TableCell>
                            <TableCell><strong>Dirección</strong></TableCell>
                            <TableCell><strong>Teléfono</strong></TableCell>
                            <TableCell align="center"><strong>Acciones</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {organizations?.map((organization) => (
                            <TableRow key={organization.orgid}>
                                <TableCell>{organization.orgnom}</TableCell>
                                <TableCell>{organization.orgdir}</TableCell>
                                <TableCell>{organization.orgtel}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Editar">
                                        <IconButton onClick={() => { setEditOrganization(organization); setOpen(true); }}>
                                            <EditIcon color="primary" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar">
                                        <IconButton onClick={() => handleOpenConfirm(organization)}>
                                            <DeleteIcon color="secondary" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Formulario de creación/edición */}
            {open && (
                <OrganizationsForm
                    open={open}
                    handleClose={() => {
                        setOpen(false);
                        setEditOrganization(null);
                    }}
                    onSubmit={handleSubmitOrganization}
                    defaultValues={editOrganization || {}}
                />
            )}

            {/* Notificaciones */}
            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setAlert({ ...alert, open: false })}
                    severity={alert.severity}
                    sx={{ width: "100%" }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>

            {/* Confirmación de eliminación */}
            <Dialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        ¿Está seguro de eliminar al cliente {" "} 
                        <strong>{selectedOrganization?.orgnom}</strong>? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancelar</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default OrganizationsDashboard;
